import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  SimpleGrid,
  Stack,
  TextInput,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { obrezano, VRSTE_POSTAVK, type VrstaPostavke } from './delavnica';
import { IzbiraCenika } from './IzbiraCenika';
import { usePravica } from '../seja/pravice';

type Nalog = Shema['DelovniNalogPodrobnosti'];

/** Začetne vrednosti okna (npr. »Razporedi ure«: delo z nerazporejenimi urami). */
export type ZacetekPostavke = { vrsta: VrstaPostavke; kolicina?: number };

/**
 * Okno »Dodaj postavko« na nalogu (brez urejanja celega naloga): vrsta, naziv iz cenika delavnice (izbira izpolni enoto,
 * ceno, DDV in podatke dela) ali novo ime, količina, cena brez DDV, popust, DDV. Ob »Dodaj« strežnik doda postavko
 * (novo ime tudi v cenik) in vrne nalog z novimi zneski.
 */
export function DodajPostavko({
  nalogId,
  zacetek,
  zapri,
  ddvStopnja,
  enote,
  dodana,
}: {
  nalogId: number;
  zacetek: ZacetekPostavke | null;
  zapri: () => void;
  ddvStopnja: number;
  enote: string[];
  dodana: (n: Nalog) => void;
}) {
  return (
    <Modal opened={!!zacetek} onClose={zapri} title="Dodaj postavko" size="lg">
      {zacetek && (
        <Obrazec
          key={`${zacetek.vrsta}-${zacetek.kolicina}`}
          nalogId={nalogId}
          zacetek={zacetek}
          zapri={zapri}
          ddvStopnja={ddvStopnja}
          enote={enote}
          dodana={dodana}
        />
      )}
    </Modal>
  );
}

function Obrazec({
  nalogId,
  zacetek,
  zapri,
  ddvStopnja,
  enote,
  dodana,
}: {
  nalogId: number;
  zacetek: ZacetekPostavke;
  zapri: () => void;
  ddvStopnja: number;
  enote: string[];
  dodana: (n: Nalog) => void;
}) {
  // nabavne cene: brez pregleda skrite, brez urejanja samo za ogled (strežnik jih ohrani)
  const nabavne = usePravica('NABAVNE_CENE');
  const urejanjeNabavnih = usePravica('NABAVNE_CENE', 'UREJANJE');
  const [vrsta, setVrsta] = useState<VrstaPostavke>(zacetek.vrsta);
  const [cenikId, setCenikId] = useState<number | undefined>();
  const [naziv, setNaziv] = useState('');
  const [kolicina, setKolicina] = useState<number | string>(zacetek.kolicina ?? 1);
  const [enota, setEnota] = useState(zacetek.vrsta === 'DELO' ? 'h' : 'kos');
  const [cena, setCena] = useState<number | string>('');
  const [popust, setPopust] = useState<number | string>(0);
  const [ddv, setDdv] = useState<number | string>(ddvStopnja);
  const [sifra, setSifra] = useState('');
  const [dobavitelj, setDobavitelj] = useState('');
  const [nabavna, setNabavna] = useState<number | string>('');
  const [predlagano, setPredlagano] = useState(false);
  const [polja, setPolja] = useState<Record<string, string>>({});

  const dodaj = useMutation({
    mutationFn: () =>
      podatki(
        api.POST('/api/delavnica/nalogi/{id}/postavke', {
          params: { path: { id: nalogId } },
          body: {
            cenikId,
            vrsta,
            naziv: obrezano(naziv),
            kolicina: typeof kolicina === 'number' ? kolicina : undefined,
            enota: obrezano(enota),
            cena: typeof cena === 'number' ? cena : undefined,
            popust: typeof popust === 'number' ? popust : undefined,
            ddvStopnja: typeof ddv === 'number' ? ddv : undefined,
            sifra: vrsta === 'DEL' ? obrezano(sifra) : undefined,
            dobavitelj: vrsta === 'DEL' ? obrezano(dobavitelj) : undefined,
            nabavnaCena: vrsta === 'DEL' && typeof nabavna === 'number' ? nabavna : undefined,
            predlagano,
          },
        }),
      ),
    onMutate: () => setPolja({}),
    onSuccess: (n) => {
      dodana(n);
      zapri();
    },
    onError: (e) => {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      }
    },
  });

  const decimalno = { decimalSeparator: ',', allowNegative: false } as const;
  return (
    <Stack gap="sm">
      <SegmentedControl
        data={VRSTE_POSTAVK}
        value={vrsta}
        onChange={(v) => {
          setVrsta(v as VrstaPostavke);
          setCenikId(undefined);
          setEnota(v === 'DELO' ? 'h' : 'kos');
        }}
      />
      <IzbiraCenika
        vrsta={vrsta}
        naziv={naziv}
        nastaviNaziv={(n) => {
          setNaziv(n);
          setCenikId(undefined);
        }}
        izbrana={(p) => {
          setCenikId(p.id);
          setNaziv(p.naziv ?? '');
          setEnota(p.enota ?? '');
          setCena(p.cena ?? '');
          setDdv(p.ddvStopnja ?? ddvStopnja);
          setSifra(p.sifra ?? '');
          setDobavitelj(p.dobavitelj ?? '');
          setNabavna(p.nabavnaCena ?? '');
        }}
        napaka={polja.naziv}
      />
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
        <NumberInput
          label="Količina"
          value={kolicina}
          onChange={setKolicina}
          error={polja.kolicina}
          decimalScale={3}
          {...decimalno}
        />
        <Autocomplete label="Enota" data={enote} value={enota} onChange={setEnota} error={polja.enota} />
        <NumberInput
          label="Cena brez DDV"
          value={cena}
          onChange={setCena}
          error={polja.cena}
          decimalScale={2}
          thousandSeparator="."
          rightSection="€"
          {...decimalno}
        />
        <NumberInput
          label="Popust"
          value={popust}
          onChange={setPopust}
          error={polja.popust}
          decimalScale={2}
          max={100}
          rightSection="%"
          {...decimalno}
        />
        <NumberInput
          label="DDV"
          value={ddv}
          onChange={setDdv}
          error={polja.ddvStopnja}
          decimalScale={2}
          max={100}
          rightSection="%"
          {...decimalno}
        />
      </SimpleGrid>
      {vrsta === 'DEL' && (
        <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
          <TextInput
            label="Šifra dela"
            value={sifra}
            onChange={(e) => setSifra(e.currentTarget.value)}
            error={polja.sifra}
          />
          <TextInput
            label="Dobavitelj"
            value={dobavitelj}
            onChange={(e) => setDobavitelj(e.currentTarget.value)}
            error={polja.dobavitelj}
          />
          {nabavne && (
            <NumberInput
              label="Nabavna cena"
              disabled={!urejanjeNabavnih}
              value={nabavna}
              onChange={setNabavna}
              error={polja.nabavnaCena}
              decimalScale={2}
              thousandSeparator="."
              rightSection="€"
              {...decimalno}
            />
          )}
        </SimpleGrid>
      )}
      <Checkbox
        label="Predlagano dodatno delo – stranka ga odobri po povezavi v e-pošti"
        checked={predlagano}
        onChange={(e) => setPredlagano(e.currentTarget.checked)}
      />
      {dodaj.error && !(dodaj.error instanceof NapakaApi && Object.keys(dodaj.error.polja).length > 0) && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(dodaj.error)}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={dodaj.isPending}>
          Prekliči
        </Button>
        <Button onClick={() => dodaj.mutate()} loading={dodaj.isPending}>
          Dodaj
        </Button>
      </Group>
    </Stack>
  );
}
